import _defineProperty from "/build/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Button } from "@/components/Buttons";
import ErrorMessage from "@/components/Form/Parts/ErrorMessage";
import { HiddenField } from "@/components/Form/Parts/index";
import { usePaymentMethodsQuery, useRecaptchaV3 } from "@/hooks";
import { PAYMENT_METHOD_ID, errorMessages } from "@/lib/constants";
import routes from "@/lib/routes";
import yupCreditCardNameValidate from "@/lib/yupCreditCardNameValidate";
import yupCreditCardNumberValidate from "@/lib/yupCreditCardNumberValidate";
import yupExpirationDateValidate from "@/lib/yupExpirationDateValidate";
import formStyles from "@/styles/Form.module.scss";
import paymentSelectStyles from "@/styles/PaymentSelect.module.scss";
import processStyles from "@/styles/Process.module.scss";
import { yupResolver } from "@hookform/resolvers/yup";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as y from "yup";
import CreditCardInput from "./CreditCardInput";
import PaymentInstruments from "./PaymentInstruments";
import PaymentMethods from "./PaymentMethods";
import RecaptchaV2 from "./RecaptchaV2";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
yupExpirationDateValidate(y);
yupCreditCardNumberValidate(y);
yupCreditCardNameValidate(y);
export const CREATE_ONLY_MODE = ["addpayment"];
export const PURCHASE_MODE = ["svod", "tvod"];
export const IGNORE_PAYPAY = ["svod", "changesvod"];
export const IGNORE_CARRIER = ["tvod", "svod", "changesvod", "addpayment"];
export default function PaymentSelectForm(_ref) {
  let {
    mode,
    onSubmit,
    paymentText,
    registerPaymentText,
    error,
    clearError,
    recaptchaSuccessV3,
    recaptchaCounterV2
  } = _ref;
  const {
    0: disableSubmitWithRecaptcha,
    1: setDisableSubmitWithRecaptcha
  } = useState(false);
  const {
    0: recaptchaTokenV2,
    1: setRecaptchaTokenV2
  } = useState();
  const {
    showRecaptchaV3Badge,
    hideRecaptchaV3Badge
  } = useRecaptchaV3();
  // recaptchaバッジの表示制御
  useEffect(() => {
    showRecaptchaV3Badge();
    return () => {
      hideRecaptchaV3Badge();
    };
  }, []);
  const {
    0: state,
    1: setState
  } = useState({
    showInput: false,
    ccInputError: false,
    paymentMethod: "",
    createPayment: CREATE_ONLY_MODE.includes(mode)
  });
  const {
    showInput,
    paymentMethod,
    createPayment
  } = state;
  useEffect(() => {
    // 支払い方法にクレジットカード選択かつ、reCAPTCHAv3が失敗したかつ、recaptchaTokenV2が未生成の場合は送信ボタンを無効化
    if (showInput && recaptchaSuccessV3 === false && !recaptchaTokenV2) {
      setDisableSubmitWithRecaptcha(true);
    } else {
      setDisableSubmitWithRecaptcha(false);
    }
  }, [recaptchaTokenV2, recaptchaSuccessV3, showInput]);
  const schema = useMemo(() => {
    const withCreditCardSchema = {
      cardNumber: y.string().creditCardNumber().required("カード番号を入力してください。"),
      expirationDate: y.string().min(6, "有効期限を選択してください。").expirationDate().required("有効期限を選択してください。"),
      cvv: y.string().min(3, "3桁または4桁で入力してください。").max(4, "3桁または4桁で入力してください。").required("セキュリティコードを入力してください。"),
      firstName: y.string().firstNameCheck().required("カード名義（英字名）を入力してください。"),
      lastName: y.string().lastNameCheck().required("カード名義（英字姓）を入力してください。"),
      mailAddress: y.string().email("メールアドレスの形式で入力してください。").required("メールアドレスが入力されていません。"),
      creditCardTerm: y.boolean().required("規約に同意してください。").oneOf([true], "規約に同意してください。")
    };
    const baseSchema = {
      paymentMethod: y.number().required("選択してください。"),
      paymentInstrument: y.number(),
      createPayment: y.boolean()
    };
    const schema = (() => {
      if (createPayment && paymentMethod === PAYMENT_METHOD_ID.CREDIT_CARD_3DS) {
        return _objectSpread(_objectSpread({}, baseSchema), withCreditCardSchema);
      } else {
        return baseSchema;
      }
    })();
    return y.object().shape(schema);
  }, [createPayment, paymentMethod]);
  const {
    register,
    handleSubmit,
    errors,
    formState,
    setValue
  } = useForm({
    resolver: yupResolver(schema),
    mode: "all"
  });
  useEffect(() => {
    register({
      name: "recaptchaType"
    });
    register({
      name: "recaptchaToken"
    });
  }, []);
  useEffect(() => {
    if (recaptchaSuccessV3 === true) {
      setValue("recaptchaType", "v3");
    } else if (recaptchaSuccessV3 === false) {
      setValue("recaptchaType", "v2");
    } else {
      setValue("recaptchaType", "v3");
    }
  }, [recaptchaSuccessV3]);
  const {
    isDirty,
    isValid,
    isSubmitting
  } = formState;
  const {
    data,
    loading
  } = usePaymentMethodsQuery();
  const paymentMethods = data === null || data === void 0 ? void 0 : data.paymentMethods;
  const paymentInstrumentData = data === null || data === void 0 ? void 0 : data.paymentInstruments;
  const onPaymentMethodChange = useCallback(newState => {
    setState(prevState => _objectSpread(_objectSpread({}, prevState), newState));
    if (clearError) clearError();
  }, [clearError]);
  if (loading) return null;
  return /*#__PURE__*/_jsxs("form", {
    onSubmit: handleSubmit(onSubmit),
    noValidate: true,
    children: [/*#__PURE__*/_jsx("div", {
      className: processStyles.contentWrapper,
      children: /*#__PURE__*/_jsxs("div", {
        className: processStyles.content,
        children: [/*#__PURE__*/_jsx("h3", {
          className: `${processStyles.contentHeader} ${processStyles.required}`,
          children: "\u304A\u652F\u6255\u3044\u65B9\u6CD5"
        }), /*#__PURE__*/_jsx(PaymentInstruments, {
          mode: mode,
          paymentInstruments: paymentInstrumentData,
          register: register,
          onChange: onPaymentMethodChange
        }), /*#__PURE__*/_jsx(PaymentMethods, {
          mode: mode,
          paymentMethods: paymentMethods,
          paymentInstruments: paymentInstrumentData,
          register: register,
          onChange: onPaymentMethodChange
        }), /*#__PURE__*/_jsx(CreditCardInput, {
          register: register,
          errors: errors,
          setValue: setValue,
          visible: showInput
        }), /*#__PURE__*/_jsx(HiddenField, {
          ref: register,
          name: "createPayment",
          value: createPayment
        }), error && error !== errorMessages["recaptcha"] && /*#__PURE__*/_jsx("div", {
          className: paymentSelectStyles.error,
          children: /*#__PURE__*/_jsx(ErrorMessage, {
            msg: error
          })
        })]
      })
    }), PURCHASE_MODE.includes(mode) && /*#__PURE__*/_jsx("div", {
      className: formStyles.guideArea,
      children: /*#__PURE__*/_jsxs("p", {
        children: ["\u203B\u7279\u5B9A\u5546\u53D6\u5F15\u6CD5\u306B\u3064\u3044\u3066", /*#__PURE__*/_jsx(Link, {
          href: routes.legalSctLaw,
          target: "_blank",
          rel: "noopener noreferrer",
          className: formStyles.textLink,
          children: "\u3053\u3061\u3089"
        }), "\u304B\u3089\u3054\u78BA\u8A8D\u304F\u3060\u3055\u3044\u3002"]
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: formStyles.btnArea,
      children: [error && error === errorMessages["recaptcha"] && !recaptchaTokenV2 && /*#__PURE__*/_jsx("div", {
        className: paymentSelectStyles.error,
        children: /*#__PURE__*/_jsx(ErrorMessage, {
          msg: error
        })
      }), recaptchaSuccessV3 === false && /*#__PURE__*/_jsx(RecaptchaV2, {
        visible: showInput,
        setValue: setValue,
        setRecaptchaTokenV2: setRecaptchaTokenV2,
        recaptchaCounterV2: recaptchaCounterV2
      }), /*#__PURE__*/_jsxs(Button, {
        type: "submit",
        fillColor: true,
        large: true,
        fontFixed: true,
        disabled: !isValid || !isDirty || disableSubmitWithRecaptcha,
        loading: isSubmitting,
        children: [!createPayment && /*#__PURE__*/_jsx(_Fragment, {
          children: paymentText
        }), createPayment && /*#__PURE__*/_jsx(_Fragment, {
          children: registerPaymentText || paymentText
        })]
      })]
    })]
  });
}